import { FunctionComponent, JSX, ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    ParentAnswer,
    Poll,
    PollAnswer,
    PollOption,
    PollOptionResult,
    PollResults,
    PollType,
    RatingDisplay
} from '@/types/NewsFeed'
import {
    BarChart as BarChartIcon,
    DonutLarge,
    HourglassBottom,
    PieChart as PieChartIcon,
    SentimentDissatisfied,
    SentimentNeutral,
    SentimentSatisfied,
    SentimentSatisfiedAlt,
    SentimentVeryDissatisfied,
    Star,
    TableRows
} from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { Role } from '@/types/Role'
import {
    ChoiceBarChartViz,
    ChoicePieChartViz,
    ChoiceRadialChartViz,
    RatingBarChartViz,
    RatingPieChartViz
} from './pollCharts'
import styles from './pollDisplay.module.scss'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useMutation } from '@apollo/client'
import { SUBMIT_POLL_ANSWER } from '@/modules/API/news/news'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import PollAnswersDialog from './pollAnswersDialog'

const POLL_TEXT_ANSWER_MAX_LENGTH: number = 500

interface PollDisplayProps {
    poll: Poll
    role: Role
    daycareId: number
    postId: number
}

const SMILEY_ICONS: ReactElement[] = [
    <SentimentVeryDissatisfied key={1} className={styles.SmileyIcon} />,
    <SentimentDissatisfied key={2} className={styles.SmileyIcon} />,
    <SentimentNeutral key={3} className={styles.SmileyIcon} />,
    <SentimentSatisfied key={4} className={styles.SmileyIcon} />,
    <SentimentSatisfiedAlt key={5} className={styles.SmileyIcon} />
]

const PollDisplay: FunctionComponent<PollDisplayProps> = ({ poll, role, daycareId, postId }: PollDisplayProps) => {
    const { t } = useTranslation()

    const isStaff: boolean = role !== Role.parent
    const results: PollResults | undefined = poll.results

    const deadlineLabel: string | null = ((): string | null => {
        if (!poll.deadline) return null
        return format(parse(String(poll.deadline), 'dd-MM-yyyy HH:mm:ss', new Date()), 'EEEE dd MMMM yyyy HH:mm', {
            locale: getLocalFromLanguage()
        })
    })()

    return (
        <div className={isStaff ? styles.ContainerDaycare : styles.ContainerParent}>
            {isStaff && !!results && !!deadlineLabel && (
                <div className={styles.Header}>
                    <span className={styles.Count}>
                        {results.totalAnswers} {t(results.totalAnswers > 1 ? 'pollAnswers' : 'pollAnswer')}
                    </span>
                    <span className={styles.Deadline}>
                        {isStaff ? <HourglassBottom /> : <span>{t('responseWaitedFor')}</span>}
                        {deadlineLabel}
                    </span>
                </div>
            )}

            {isStaff ? (
                <StaffResults poll={poll} results={results} />
            ) : (
                <ParentView poll={poll} daycareId={daycareId} postId={postId} />
            )}
        </div>
    )
}

type ChoiceVizType = 'bars' | 'bar_chart' | 'pie' | 'radial'
type RatingVizType = 'bars' | 'bar_chart' | 'radial'

interface StaffResultsProps {
    poll: Poll
    results?: PollResults
}

function StaffResults({ poll, results }: Readonly<StaffResultsProps>): ReactElement {
    const { t } = useTranslation()
    const [choiceViz, setChoiceViz] = useState<ChoiceVizType>('bars')
    const [showAnswers, setShowAnswers] = useState<boolean>(false)
    const [ratingViz, setRatingViz] = useState<RatingVizType>('bars')

    if (!results) {
        return <p className={styles.Empty}>{t('noAnswers')}</p>
    }

    const hasVoterDetails: boolean = !poll.anonymous && !!results.parentAnswers && results.parentAnswers.length > 0

    if (poll.type === PollType.text_free) {
        return (
            <>
                {!poll.deadline && (
                    <div className={styles.Header}>
                        <span className={styles.Count}>
                            {results.totalAnswers} {t(results.totalAnswers > 1 ? 'pollAnswers' : 'pollAnswer')}
                        </span>
                    </div>
                )}
                <ul className={styles.TextAnswers}>
                    {hasVoterDetails && results.parentAnswers
                        ? results.parentAnswers.map(
                              (pa: ParentAnswer, i: number): JSX.Element => (
                                  <li key={i} className={styles.TextAnswer}>
                                      <span>{pa.textAnswer}</span>
                                      <span className={styles.VoterName}>
                                          {' - '}
                                          {pa.parentName}
                                      </span>
                                  </li>
                              )
                          )
                        : results.textAnswers?.map(
                              (answer: string, i: number): JSX.Element => (
                                  <li key={answer.length + '_' + i} className={styles.TextAnswer}>
                                      <span>{answer}</span>
                                  </li>
                              )
                          )}
                </ul>
            </>
        )
    }

    if (poll.type === PollType.rating) {
        return (
            <>
                <div
                    className={hasVoterDetails ? styles.ClickableResults : undefined}
                    role={hasVoterDetails ? 'button' : undefined}
                    tabIndex={hasVoterDetails ? 0 : undefined}
                    aria-label={hasVoterDetails ? t('pollAnswersTitle') : undefined}
                    onClick={() => hasVoterDetails && setShowAnswers(true)}
                    onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault()
                            if (hasVoterDetails) setShowAnswers(true)
                        }
                    }}
                >
                    <div className={styles.Header}>
                        {!poll.deadline && (
                            <div className={styles.CountResult}>
                                <span className={styles.Count}>
                                    {results.totalAnswers} {t(results.totalAnswers > 1 ? 'pollAnswers' : 'pollAnswer')}
                                </span>
                            </div>
                        )}
                        {poll.hasVotes && (
                            <div className={styles.VizSwitcher} onClick={(event) => event.stopPropagation()}>
                                <IconButton
                                    size='small'
                                    onClick={() => setRatingViz('bars')}
                                    color={ratingViz === 'bars' ? 'primary' : 'default'}
                                >
                                    <TableRows fontSize='small' />
                                </IconButton>
                                <IconButton
                                    size='small'
                                    onClick={() => setRatingViz('bar_chart')}
                                    color={ratingViz === 'bar_chart' ? 'primary' : 'default'}
                                >
                                    <BarChartIcon fontSize='small' />
                                </IconButton>
                                <IconButton
                                    size='small'
                                    onClick={() => setRatingViz('radial')}
                                    color={ratingViz === 'radial' ? 'primary' : 'default'}
                                >
                                    <PieChartIcon fontSize='small' />
                                </IconButton>
                            </div>
                        )}
                    </div>
                    {ratingViz === 'bars' && <RatingResults poll={poll} results={results} t={t} />}
                    {ratingViz === 'bar_chart' && (
                        <RatingBarChartViz
                            results={results}
                            ratingDisplay={poll.ratingDisplay ?? RatingDisplay.stars}
                        />
                    )}
                    {ratingViz === 'radial' && (
                        <RatingPieChartViz
                            results={results}
                            ratingDisplay={poll.ratingDisplay ?? RatingDisplay.stars}
                        />
                    )}
                </div>
                {hasVoterDetails && (
                    <PollAnswersDialog
                        open={showAnswers}
                        handleClose={() => setShowAnswers(false)}
                        poll={poll}
                        results={results}
                    />
                )}
            </>
        )
    }

    // single_choice / multiple_choice
    const choiceDenominator: number =
        poll.type === PollType.multiple_choice
            ? Math.max(...results.options.map((o: PollOptionResult) => o.count), 0)
            : results.totalAnswers

    return (
        <>
            <div
                className={hasVoterDetails ? styles.ClickableResults : undefined}
                role={hasVoterDetails ? 'button' : undefined}
                tabIndex={hasVoterDetails ? 0 : undefined}
                aria-label={hasVoterDetails ? t('pollAnswersTitle') : undefined}
                onClick={() => hasVoterDetails && setShowAnswers(true)}
                onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault()
                        if (hasVoterDetails) setShowAnswers(true)
                    }
                }}
            >
                <div className={styles.Header}>
                    {!poll.deadline && (
                        <div className={styles.CountResult}>
                            <span className={styles.Count}>
                                {results.totalAnswers} {t(results.totalAnswers > 1 ? 'pollAnswers' : 'pollAnswer')}
                            </span>
                        </div>
                    )}
                    {poll.hasVotes && (
                        <div className={styles.VizSwitcher} onClick={(event) => event.stopPropagation()}>
                            <IconButton
                                size='small'
                                onClick={() => setChoiceViz('bars')}
                                color={choiceViz === 'bars' ? 'primary' : 'default'}
                            >
                                <TableRows fontSize='small' />
                            </IconButton>
                            <IconButton
                                size='small'
                                onClick={() => setChoiceViz('bar_chart')}
                                color={choiceViz === 'bar_chart' ? 'primary' : 'default'}
                            >
                                <BarChartIcon fontSize='small' />
                            </IconButton>
                            <IconButton
                                size='small'
                                onClick={() => setChoiceViz('pie')}
                                color={choiceViz === 'pie' ? 'primary' : 'default'}
                            >
                                <PieChartIcon fontSize='small' />
                            </IconButton>
                            <IconButton
                                size='small'
                                onClick={() => setChoiceViz('radial')}
                                color={choiceViz === 'radial' ? 'primary' : 'default'}
                            >
                                <DonutLarge fontSize='small' />
                            </IconButton>
                        </div>
                    )}
                </div>
                {choiceViz === 'bars' && (
                    <div className={styles.Options}>
                        {results.options.map((opt: PollOptionResult): JSX.Element => {
                            const pct: number =
                                choiceDenominator > 0 ? Math.round((opt.count / choiceDenominator) * 100) : 0
                            return (
                                <div key={opt.id} className={styles.OptionRow}>
                                    <div className={styles.OptionLabel}>
                                        <span>{opt.text || `Option ${opt.order + 1}`}</span>
                                        <span className={styles.OptionCount}>
                                            {opt.count} ({pct}%)
                                        </span>
                                    </div>
                                    <div className={styles.ProgressBar}>
                                        <div className={styles.ProgressFill} style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
                {choiceViz === 'bar_chart' && (
                    <ChoiceBarChartViz options={results.options} totalAnswers={choiceDenominator} />
                )}
                {choiceViz === 'pie' && (
                    <ChoicePieChartViz options={results.options} totalAnswers={choiceDenominator} />
                )}
                {choiceViz === 'radial' && (
                    <ChoiceRadialChartViz options={results.options} totalAnswers={choiceDenominator} />
                )}
            </div>
            {hasVoterDetails && (
                <PollAnswersDialog
                    open={showAnswers}
                    handleClose={() => setShowAnswers(false)}
                    poll={poll}
                    results={results}
                />
            )}
        </>
    )
}

interface RatingResultsProps {
    poll: Poll
    results: PollResults
    t: (key: string) => string
}

function RatingResults({ poll, results, t }: Readonly<RatingResultsProps>): ReactElement {
    const display: RatingDisplay = poll.ratingDisplay ?? RatingDisplay.stars
    const avg: number | undefined = results.averageRating
    const maxRating: number = 5
    const hasVoterDetails: boolean = !poll.anonymous && !!results.parentAnswers && results.parentAnswers.length > 0

    return (
        <div className={styles.RatingResults}>
            {avg && avg >= 1 && (
                <div className={styles.AverageRow}>
                    <span className={styles.AverageLabel}>{t('average')}:</span>
                    {display !== RatingDisplay.numbers && (
                        <RatingVisual display={display} value={avg} max={maxRating} fraction={1} />
                    )}
                    <span className={styles.AverageValue}>
                        {avg.toFixed(1)} / {maxRating}
                    </span>
                </div>
            )}
            <div className={styles.RatingBars}>
                {Array.from({ length: maxRating }, (_: unknown, i: number): JSX.Element => {
                    const rating: number = i + 1
                    const ratingParents: ParentAnswer[] = hasVoterDetails
                        ? (results.parentAnswers ?? [])
                              .filter((a: ParentAnswer): boolean => a.rating === rating)
                              .sort((a: ParentAnswer, b: ParentAnswer): number =>
                                  a.parentName.localeCompare(b.parentName)
                              )
                        : []
                    const count: number = hasVoterDetails
                        ? ratingParents.length
                        : (results.options.find((o: PollOptionResult): boolean => o.order === i)?.count ?? 0)
                    const pct: number = results.totalAnswers > 0 ? Math.round((count / results.totalAnswers) * 100) : 0

                    const barRow: JSX.Element = (
                        <div key={rating} className={styles.RatingBarRow}>
                            <span className={styles.RatingLabel}>
                                {display === RatingDisplay.smileys ? (
                                    SMILEY_ICONS[i]
                                ) : display === RatingDisplay.stars ? (
                                    <>
                                        <Star fontSize='inherit' />
                                        {rating}
                                    </>
                                ) : (
                                    rating
                                )}
                            </span>
                            <div className={styles.ProgressBar}>
                                <div className={styles.ProgressFill} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={styles.RatingCount}>{count}</span>
                        </div>
                    )

                    return barRow
                })}
            </div>
        </div>
    )
}

interface RatingVisualProps {
    display: RatingDisplay
    value: number
    max: number
    fraction: number
}

function RatingVisual({ display, value, max, fraction }: Readonly<RatingVisualProps>): ReactElement {
    if (display === RatingDisplay.numbers) {
        return <span className={styles.AverageNumber}>{value.toFixed(fraction)}</span>
    }

    return (
        <div className={styles.Stars}>
            {Array.from({ length: max }, (_: unknown, i: number): JSX.Element => {
                const filled: boolean = i < Math.round(value)
                if (display === RatingDisplay.smileys) {
                    return (
                        <span key={i} className={filled ? styles.StarFilled : styles.StarEmpty}>
                            {SMILEY_ICONS[i]}
                        </span>
                    )
                }
                return <Star key={i} className={filled ? styles.StarFilled : styles.StarEmpty} />
            })}
        </div>
    )
}

interface ParentViewProps {
    poll: Poll
    daycareId: number
    postId: number
}

function ParentView({ poll, daycareId, postId }: Readonly<ParentViewProps>): ReactElement {
    const { t } = useTranslation()
    const [submitPollAnswer] = useMutation(SUBMIT_POLL_ANSWER)

    const [draft, setDraft] = useState<PollAnswer>(poll.myAnswer ?? {})
    const [submitted, setSubmitted] = useState<PollAnswer | undefined>(poll.myAnswer)
    const [isEditing, setIsEditing] = useState<boolean>(!poll.myAnswer)
    const [loading, setLoading] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<boolean>(false)

    const isPastDeadline: boolean =
        !!poll.deadline && parse(String(poll.deadline), 'dd-MM-yyyy HH:mm:ss', new Date()) < new Date()

    function handleSubmit(): void {
        setLoading(true)
        setSubmitError(false)
        submitPollAnswer({
            variables: {
                daycareId,
                postId,
                textAnswer: draft.textAnswer,
                rating: draft.rating,
                optionIds: draft.selectedOptionIds
            }
        })
            .then(() => {
                setSubmitted(draft)
                setIsEditing(false)
            })
            .catch(() => {
                setSubmitError(true)
            })
            .finally(() => setLoading(false))
    }

    if (!isEditing && submitted) {
        return (
            <AnswerReadView
                poll={poll}
                answer={submitted}
                isPastDeadline={isPastDeadline}
                onEdit={() => {
                    setDraft(submitted)
                    setIsEditing(true)
                }}
                t={t}
            />
        )
    }

    if (isPastDeadline) {
        return <p className={styles.Empty}>{t('noAnswerDeadlinePassed')}</p>
    }

    return (
        <>
            {submitError && <p className={styles.SubmitError}>{t('submitError')}</p>}
            <AnswerForm poll={poll} draft={draft} onChange={setDraft} onSubmit={handleSubmit} loading={loading} />
        </>
    )
}

interface AnswerReadViewProps {
    poll: Poll
    answer: PollAnswer
    isPastDeadline: boolean
    onEdit: () => void
    t: (key: string) => string
}

function AnswerReadView({ poll, answer, isPastDeadline, onEdit, t }: Readonly<AnswerReadViewProps>): ReactElement {
    return (
        <div className={styles.AnswerReadView}>
            {poll.type === PollType.text_free && (
                <div className={styles.MyChoices}>
                    <span className={styles.MyChoicesLabel}>{t('yourAnswer')}:</span>
                    <p className={styles.MyAnswer}>{answer.textAnswer}</p>
                </div>
            )}
            {poll.type === PollType.rating && (
                <div className={styles.MyRating}>
                    <span>{t('yourAnswer')}:</span>
                    <RatingVisual
                        display={poll.ratingDisplay ?? RatingDisplay.stars}
                        value={answer.rating ?? 0}
                        max={5}
                        fraction={0}
                    />
                </div>
            )}
            {(poll.type === PollType.single_choice || poll.type === PollType.multiple_choice) && (
                <div className={styles.MyChoices}>
                    <span className={styles.MyChoicesLabel}>{t('yourAnswer')}:</span>
                    {poll.options
                        .filter((o: PollOption): boolean => (answer.selectedOptionIds ?? []).includes(o.id))
                        .map(
                            (o: PollOption): JSX.Element => (
                                <span key={o.id} className={styles.MyChoice}>
                                    {o.text}
                                </span>
                            )
                        )}
                </div>
            )}
            {!isPastDeadline && (
                <div className={styles.EditButtonWrapper}>
                    <ButtonTextIcon type='edit' parent onClick={onEdit} className={styles.Button} />
                </div>
            )}
        </div>
    )
}

interface AnswerFormProps {
    poll: Poll
    draft: PollAnswer
    onChange: (answer: PollAnswer) => void
    onSubmit: () => void
    loading: boolean
}

function AnswerForm({ poll, draft, onChange, onSubmit, loading }: Readonly<AnswerFormProps>): ReactElement {
    const isSubmitDisabled: boolean = loading || !isDraftValid(poll, draft)

    return (
        <div className={styles.AnswerForm}>
            {poll.type === PollType.text_free && (
                <TextArea
                    className={styles.TextInput}
                    value={draft.textAnswer ?? ''}
                    onChange={(e: string): void => onChange({ ...draft, textAnswer: e })}
                    maxCount={POLL_TEXT_ANSWER_MAX_LENGTH}
                    parent
                />
            )}
            {poll.type === PollType.single_choice && (
                <div className={styles.ChoiceList}>
                    {poll.options.map(
                        (opt: PollOption): JSX.Element => (
                            <div
                                key={opt.id}
                                className={`${styles.ChoiceOption} ${draft.selectedOptionIds?.includes(opt.id) ? styles.ChoiceOptionSelected : ''}`}
                                onClick={() => onChange({ ...draft, selectedOptionIds: [opt.id] })}
                            >
                                {opt.text}
                            </div>
                        )
                    )}
                </div>
            )}
            {poll.type === PollType.multiple_choice && (
                <div className={styles.ChoiceList}>
                    {poll.options.map(
                        (opt: PollOption): JSX.Element => (
                            <Checkbox
                                key={opt.id}
                                parent
                                checked={(draft.selectedOptionIds ?? []).includes(opt.id)}
                                text={opt.text}
                                onChange={(checked: boolean): void => {
                                    const ids: number[] = draft.selectedOptionIds ?? []
                                    onChange({
                                        ...draft,
                                        selectedOptionIds: checked
                                            ? [...ids, opt.id]
                                            : ids.filter((id: number): boolean => id !== opt.id)
                                    })
                                }}
                            />
                        )
                    )}
                </div>
            )}
            {poll.type === PollType.rating && (
                <RatingInput
                    display={poll.ratingDisplay ?? RatingDisplay.stars}
                    value={draft.rating ?? 0}
                    max={5}
                    onChange={(rating: number) => onChange({ ...draft, rating })}
                />
            )}
            <div className={styles.EditButtonWrapper}>
                <ButtonTextIcon
                    type='confirm'
                    parent
                    onClick={onSubmit}
                    disabled={isSubmitDisabled}
                    className={styles.Button}
                />
            </div>
        </div>
    )
}

function isDraftValid(poll: Poll, draft: PollAnswer): boolean {
    switch (poll.type) {
        case PollType.text_free:
            return !!draft.textAnswer?.trim()
        case PollType.single_choice:
            return (draft.selectedOptionIds?.length ?? 0) === 1
        case PollType.multiple_choice:
            return (draft.selectedOptionIds?.length ?? 0) > 0
        case PollType.rating:
            return (draft.rating ?? 0) > 0
        default:
            return false
    }
}

interface RatingInputProps {
    display: RatingDisplay
    value: number
    max: number
    onChange: (rating: number) => void
}

function RatingInput({ display, value, max, onChange }: Readonly<RatingInputProps>): ReactElement {
    return (
        <div className={styles.RatingInput}>
            {Array.from({ length: max }, (_: unknown, i: number) => {
                const rating: number = i + 1
                const filled: boolean = rating <= value
                if (display === RatingDisplay.smileys) {
                    return (
                        <span
                            key={i}
                            className={`${styles.RatingInputItem} ${filled ? styles.StarFilled : styles.StarEmpty}`}
                            onClick={(): void => onChange(rating)}
                        >
                            {SMILEY_ICONS[i]}
                        </span>
                    )
                }
                if (display === RatingDisplay.numbers) {
                    return (
                        <span
                            key={i}
                            className={`${styles.RatingInputNumber} ${filled ? styles.RatingInputNumberSelected : ''}`}
                            onClick={(): void => onChange(rating)}
                        >
                            {rating}
                        </span>
                    )
                }
                return (
                    <Star
                        key={i}
                        fontSize='small'
                        className={`${styles.RatingInputItem} ${filled ? styles.StarFilled : styles.StarEmpty}`}
                        onClick={(): void => onChange(rating)}
                    />
                )
            })}
        </div>
    )
}

export default PollDisplay
